import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import listPlugin from "@fullcalendar/list";

import { AppShell } from "@/components/AppShell";
import { api, extractErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton, EmptyState } from "@/components/EmptyState";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import { toast } from "sonner";
import { useLocations } from "@/lib/locations";
import {
  Calendar as CalIcon, Filter, CalendarClock, Factory, User, Package, ChevronRight, AlertTriangle,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const STATUS_LEGEND = [
  { status: "draft", color: "#94A3B8", label: "Draft" },
  { status: "confirmed", color: "#2563EB", label: "Confirmed" },
  { status: "in_progress", color: "#F59E0B", label: "In Progress" },
  { status: "completed", color: "#10B981", label: "Completed" },
  { status: "shipped", color: "#0EA5E9", label: "Shipped" },
  { status: "overdue", color: "#E11D48", label: "Overdue" },
];

export default function ProductionCalendar() {
  const locCtx = useLocations() || {};
  const { activeId } = locCtx;
  const [events, setEvents] = useState([]);
  const [unscheduled, setUnscheduled] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: "all",
    priority: "all",
    customer: "",
    product: "",
    species: "",
  });
  const [selected, setSelected] = useState(null);
  const calRef = useRef(null);
  const rangeRef = useRef({ start: null, end: null });
  const navigate = useNavigate();

  const load = useCallback(async (range) => {
    const r = range || rangeRef.current || {};
    if (!r.start || !r.end) return;
    setLoading(true);
    try {
      const params = { start: r.start, end: r.end };
      if (filters.status !== "all") params.status = filters.status;
      if (filters.priority !== "all") params.priority = filters.priority;
      if (filters.customer) params.customer = filters.customer;
      if (filters.product) params.product = filters.product;
      if (filters.species) params.species = filters.species;
      if (activeId && activeId !== "all") params.location_id = activeId;
      const { data } = await api.get("/production/calendar", { params });
      setEvents(data.events || []);
      setUnscheduled(data.unscheduled || []);
    } catch (e) {
      toast.error(extractErr(e, "Failed to load calendar"));
    } finally {
      setLoading(false);
    }
  }, [filters, activeId]);

  // Debounce filter changes to avoid hammering the API on every keystroke
  useEffect(() => {
    const t = setTimeout(() => load(), 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.status, filters.priority, filters.customer, filters.product, filters.species, activeId]);

  const onDatesSet = useCallback((info) => {
    // FullCalendar gives us start/end as Date
    const s = info.start.toISOString();
    const e = info.end.toISOString();
    rangeRef.current = { start: s, end: e };
    load({ start: s, end: e });
  }, [load]);

  const onEventClick = useCallback((info) => {
    setSelected(info.event.extendedProps);
  }, []);

  const onEventDrop = useCallback(async (info) => {
    const { event } = info;
    const newDate = event.start ? event.start.toISOString() : null;
    const orderId = event.id;
    try {
      await api.patch(`/production/orders/${orderId}/production-date`, { production_date: newDate });
      toast.success(`Rescheduled to ${event.start.toLocaleDateString()}`);
      // No need to refetch; FullCalendar already has the updated event
    } catch (e) {
      info.revert();
      toast.error(extractErr(e, "Failed to reschedule"));
    }
  }, []);

  const schedule = async (orderId, dateIso) => {
    try {
      await api.patch(`/production/orders/${orderId}/production-date`, { production_date: dateIso });
      toast.success("Scheduled");
      await load();
    } catch (e) {
      toast.error(extractErr(e, "Failed to schedule"));
    }
  };

  // Visual legend header action
  const legend = (
    <div className="hidden md:flex items-center gap-1.5 text-[11px]">
      {STATUS_LEGEND.map((s) => (
        <span key={s.status} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-muted/60">
          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: s.color }} />
          {s.label}
        </span>
      ))}
    </div>
  );

  return (
    <AppShell
      title="Production Calendar"
      subtitle="Plan jobs by date — drag to reschedule"
      headerActions={legend}
    >
      {/* Filters */}
      <div className="rounded-2xl border border-border bg-card p-3 mb-3">
        <div className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-muted-foreground mb-2">
          <Filter className="w-3.5 h-3.5" /> Filters
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          <div className="space-y-1.5">
            <Label>Status</Label>
            <Select value={filters.status} onValueChange={(v) => setFilters((p) => ({ ...p, status: v }))}>
              <SelectTrigger data-testid="cal-filter-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="confirmed">Confirmed</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="shipped">Shipped</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Priority</Label>
            <Select value={filters.priority} onValueChange={(v) => setFilters((p) => ({ ...p, priority: v }))}>
              <SelectTrigger data-testid="cal-filter-priority"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any priority</SelectItem>
                <SelectItem value="low">Low</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="urgent">Urgent</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Customer</Label>
            <Input placeholder="Search customer…" value={filters.customer} onChange={(e) => setFilters((p) => ({ ...p, customer: e.target.value }))} data-testid="cal-filter-customer" />
          </div>
          <div className="space-y-1.5">
            <Label>Product</Label>
            <Input placeholder="Search product…" value={filters.product} onChange={(e) => setFilters((p) => ({ ...p, product: e.target.value }))} data-testid="cal-filter-product" />
          </div>
          <div className="space-y-1.5">
            <Label>Species</Label>
            <Input placeholder="Search species…" value={filters.species} onChange={(e) => setFilters((p) => ({ ...p, species: e.target.value }))} data-testid="cal-filter-species" />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[1fr_320px] gap-3">
        {/* Calendar */}
        <div className="rounded-2xl border border-border bg-card p-2 md:p-3" data-testid="production-calendar">
          {loading && events.length === 0 ? <Skeleton className="h-[540px]" /> : null}
          <FullCalendar
            ref={calRef}
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin]}
            initialView="dayGridMonth"
            headerToolbar={{ left: "prev,next today", center: "title", right: "dayGridMonth,timeGridWeek,timeGridDay,listWeek" }}
            events={events}
            editable={true}
            droppable={true}
            height={"auto"}
            eventClick={onEventClick}
            eventDrop={onEventDrop}
            datesSet={onDatesSet}
            dayMaxEventRows={4}
            firstDay={1}
            eventDidMount={(info) => {
              // Accessibility: add a tooltip with additional detail
              const p = info.event.extendedProps;
              const parts = [
                p.customer,
                p.status && `Status: ${p.status}`,
                p.priority && `Priority: ${p.priority}`,
                p.total_quantity && `Qty: ${p.total_quantity}`,
                p.overdue && "OVERDUE",
              ].filter(Boolean);
              info.el.setAttribute("title", parts.join(" • "));
              info.el.setAttribute("data-testid", `calendar-event-${info.event.id}`);
            }}
          />
        </div>

        {/* Unscheduled sidebar */}
        <div className="rounded-2xl border border-border bg-card p-3" data-testid="calendar-unscheduled">
          <div className="flex items-center gap-1.5 mb-2 text-sm font-medium">
            <CalendarClock className="w-4 h-4 text-primary" /> Unscheduled
            <span className="text-xs font-mono text-muted-foreground ml-auto">{unscheduled.length}</span>
          </div>
          {unscheduled.length === 0 ? (
            <div className="text-xs text-muted-foreground py-8 text-center">All orders are scheduled.</div>
          ) : (
            <div className="space-y-2 max-h-[540px] overflow-y-auto -mx-1 px-1">
              {unscheduled.map((u) => (
                <UnscheduledRow key={u.id} order={u} onOpen={() => setSelected({ order_id: u.id, ...u })} onSchedule={(iso) => schedule(u.id, iso)} />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Event detail sheet */}
      <Sheet open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto" data-testid="calendar-event-sheet">
          {selected && (
            <>
              <SheetHeader>
                <SheetTitle className="flex items-center gap-2">
                  <Factory className="w-5 h-5 text-primary" /> {selected.order_number || "Order"}
                </SheetTitle>
                <SheetDescription>{selected.customer || "—"}</SheetDescription>
              </SheetHeader>
              <div className="mt-4 space-y-3 text-sm">
                {selected.overdue && (
                  <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-rose-500/10 text-rose-600 dark:text-rose-400 border border-rose-500/30">
                    <AlertTriangle className="w-4 h-4" /> This order is overdue
                  </div>
                )}
                <InfoRow label="Status" value={(selected.status || "—").replace(/_/g, " ")} />
                <InfoRow label="Priority" value={selected.priority || "—"} />
                {selected.first_product && <InfoRow label="Product" value={selected.first_product + (selected.first_size ? ` • ${selected.first_size}` : "")} />}
                {selected.first_species && <InfoRow label="Species" value={selected.first_species} />}
                {selected.total_quantity != null && <InfoRow label="Quantity" value={selected.total_quantity} />}
                {selected.line_count != null && <InfoRow label="Lines" value={selected.line_count} />}
                {selected.notes && <InfoRow label="Notes" value={selected.notes} />}
              </div>
              <div className="mt-5 flex gap-2">
                <Button variant="outline" className="flex-1" onClick={() => setSelected(null)}>Close</Button>
                <Button className="flex-1" onClick={() => { setSelected(null); navigate("/app/production"); }} data-testid="calendar-event-open">
                  Open order <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      <style>{`
        /* FullCalendar + theme tokens */
        .fc { font-family: inherit; }
        .fc .fc-toolbar-title { font-family: var(--font-heading), inherit; font-weight: 600; }
        .fc .fc-button { background: hsl(var(--primary)); border-color: hsl(var(--primary)); color: hsl(var(--primary-foreground)); text-transform: capitalize; padding: 4px 10px; font-size: 12px; }
        .fc .fc-button:hover { background: hsl(var(--primary) / 0.9); }
        .fc .fc-button-primary:not(:disabled).fc-button-active { background: hsl(var(--primary) / 0.85); }
        .fc .fc-daygrid-day-number, .fc .fc-col-header-cell-cushion { color: hsl(var(--foreground)); text-decoration: none; }
        .fc-theme-standard td, .fc-theme-standard th, .fc-theme-standard .fc-scrollgrid { border-color: hsl(var(--border)); }
        .fc-day-today { background: hsl(var(--primary) / 0.06) !important; }
        .fc-event { cursor: pointer; border: none; padding: 2px 4px; font-size: 11px; border-radius: 6px; }
        .fc-list-day-cushion { background: hsl(var(--muted) / 0.4) !important; }
        .fc-popover { background: hsl(var(--card)); color: hsl(var(--foreground)); border: 1px solid hsl(var(--border)); }
        .fc-popover-header { background: hsl(var(--muted) / 0.4); }
        .fc .fc-list-empty { background: transparent; color: hsl(var(--muted-foreground)); }
      `}</style>
    </AppShell>
  );
}

function InfoRow({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-2 border-b border-border pb-1.5">
      <span className="text-xs uppercase tracking-wider text-muted-foreground">{label}</span>
      <span className="text-sm font-medium capitalize text-right">{value}</span>
    </div>
  );
}

function UnscheduledRow({ order, onOpen, onSchedule }) {
  const [date, setDate] = useState("");
  const tone = useMemo(() => {
    if (order.priority === "urgent" || order.priority === "high") return "border-l-amber-500";
    return "border-l-primary";
  }, [order.priority]);
  const save = (e) => {
    e?.preventDefault?.();
    if (!date) { toast.error("Pick a date"); return; }
    const iso = new Date(date).toISOString();
    setDate("");
    onSchedule(iso);
  };
  return (
    <div className={`rounded-lg border border-border border-l-4 ${tone} p-2 bg-background`} data-testid={`calendar-unscheduled-${order.id}`}>
      <button type="button" onClick={onOpen} className="block text-left w-full">
        <div className="flex items-center gap-2">
          <span className="text-[11px] font-mono text-muted-foreground">{order.order_number}</span>
          <span className="text-[10px] rounded-full px-1.5 py-0.5 bg-muted capitalize">{(order.status || "").replace("_", " ")}</span>
        </div>
        <div className="text-sm font-medium truncate">{order.customer}</div>
        <div className="text-[11px] text-muted-foreground">
          {order.line_count} line{order.line_count === 1 ? "" : "s"} • {order.total_quantity} units
        </div>
      </button>
      <form onSubmit={save} className="flex items-center gap-1.5 mt-2">
        <Input type="date" className="h-7 text-xs" value={date} onChange={(e) => setDate(e.target.value)} data-testid={`calendar-schedule-input-${order.id}`} />
        <Button type="submit" size="sm" variant="secondary" className="h-7" data-testid={`calendar-schedule-btn-${order.id}`}>Schedule</Button>
      </form>
    </div>
  );
}
